import { useState, useCallback } from 'react';
import { ToastContainer, ToastOptions, UpdateOptions, Id, toast } from 'react-toastify';

const baseToastOptions: ToastOptions = {
  position: 'top-center',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true
};

export const showToastError = (message: string, options?: ToastOptions) =>
  toast.error(message, { ...baseToastOptions, ...options });

export const showToastSuccess = (message: string, options?: ToastOptions) =>
  toast.success(message, { ...baseToastOptions, ...options });

export const showToastWarning = (message: string, options?: ToastOptions) =>
  toast.warning(message, { ...baseToastOptions, ...options });

export const showToastInfo = (message: string, options?: ToastOptions) =>
  toast.info(message, { ...baseToastOptions, ...options });

export const updateToast = (toastId: Id, options: UpdateOptions) =>
  toast.update(toastId, options);

export const dismissToast = (toastId?: Id) =>
  toast.dismiss(toastId);

export interface ProgressToast {
  id: Id;
  report: (percent: number, label: string) => void;
  /** Eases the bar from its current value toward `target`. Returns stop(). */
  animateTo: (target: number, label: string, intervalMs?: number, easeFactor?: number) => () => void;
  succeed: (label: string, autoCloseMs?: number) => void;
  fail: (label: string, autoCloseMs?: number) => void;
  dismiss: () => void;
}

const lockedOptions = {
  autoClose: false as const,
  closeOnClick: false,
  draggable: false,
  closeButton: false,
};

const unlockedOptions = {
  closeOnClick: true,
  draggable: true,
  closeButton: true,
};

export function startProgressToast(initialLabel: string): ProgressToast {
  const id = showToastInfo(`${initialLabel} 0%`, lockedOptions);

  // One-shot guard: after succeed/fail/dismiss, further calls are no-ops.
  let settled = false;
  let lastReported = 0;

  const report = (percent: number, label: string) => {
    if (settled) return;
    const clamped = Math.max(0, Math.min(1, percent));
    lastReported = clamped;
    updateToast(id, {
      render: `${label} ${Math.round(clamped * 100)}%`,
      progress: clamped,
      type: 'info',
      ...lockedOptions,
    });
  };

  const animateTo = (target: number, label: string, intervalMs = 300, easeFactor = 0.05) => {
    let current = lastReported;
    report(current, label);
    const timer = setInterval(() => {
      if (settled) {
        clearInterval(timer);
        return;
      }
      current += (target - current) * easeFactor;
      report(current, label);
    }, intervalMs);
    return () => clearInterval(timer);
  };

  const succeed = (label: string, autoCloseMs = 3000) => {
    if (settled) return;
    settled = true;
    updateToast(id, {
      render: `${label} 100%`,
      type: 'success',
      progress: undefined,
      autoClose: autoCloseMs,
      ...unlockedOptions,
    });
  };

  const fail = (label: string, autoCloseMs = 5000) => {
    if (settled) return;
    settled = true;
    updateToast(id, {
      render: label,
      type: 'error',
      progress: undefined,
      autoClose: autoCloseMs,
      ...unlockedOptions,
    });
  };

  const dismiss = () => {
    if (settled) return;
    settled = true;
    dismissToast(id);
  };

  return { id, report, animateTo, succeed, fail, dismiss };
}

export default function ToastNotifications() {
  return <ToastContainer {...baseToastOptions} newestOnTop limit={4} />;
}

interface ToastInlineProps {
  message: string | null;
  onDismiss?: () => void;
  testId?: string;
}

export function ToastInline({ message, onDismiss, testId }: ToastInlineProps) {
  if (!message) return null;
  return (
    <div className="alert error" role="alert" data-testid={testId} style={{ marginBottom: 12, padding: '0px 12px', gridColumn: '1 / -1' }}>
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
        <rect x="8" y="8" width="40" height="40" rx="16" fill="#FBE4E7"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M18.0866 26.5838L26.5838 18.0866C27.3659 17.3045 28.6341 17.3045 29.4162 18.0866L37.9134 26.5838C38.6955 27.3659 38.6955 28.6341 37.9134 29.4162L29.4162 37.9134C28.6341 38.6955 27.3659 38.6955 26.5838 37.9134L18.0866 29.4162C17.3045 28.6341 17.3045 27.3659 18.0866 26.5838ZM27.9975 34.0333C27.157 34.0333 26.48 33.3567 26.48 32.5167C26.48 31.6767 27.157 31 27.9975 31C28.838 31 29.515 31.6767 29.515 32.5167C29.515 33.3567 28.838 34.0333 27.9975 34.0333ZM26.835 28.3333C26.835 28.975 27.3603 29.5 28.0023 29.5C28.6443 29.5 29.1696 28.975 29.1696 28.3333V23.6667C29.1696 23.025 28.6443 22.5 28.0023 22.5C27.3603 22.5 26.835 23.025 26.835 23.6667V28.3333Z" fill="#DC0C22"/>
      </svg>
      <span style={{ flex: 1 }} data-testid={testId ? `${testId}-message` : undefined}>{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          data-testid={testId ? `${testId}-dismiss` : undefined}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 18, lineHeight: 1, padding: '0 0 0 8px' }}
        >
          ×
        </button>
      )}
    </div>
  );
}

export function useToastInline() {
  const [message, setMessage] = useState<string | null>(null);
  const showToastInline = useCallback((msg: string) => setMessage(msg), []);
  const hideToastInline = useCallback(() => setMessage(null), []);
  return { message, showToastInline, hideToastInline };
}
